<script lang="ts">
  import type { CheckReport, Profile, RuleInfo } from '@gostword/engine';
  import Footer from './components/Footer.svelte';
  import Header from './components/Header.svelte';
  import ImportDialog from './components/ImportDialog.svelte';
  import Landing from './components/Landing.svelte';
  import ProfileDialog from './components/ProfileDialog.svelte';
  import ReportView from './components/ReportView.svelte';
  import { deliverFile } from './lib/download';
  import { EngineClient, type FixRequest, type FixResponse } from './lib/engine';
  import { baseName } from './lib/format';
  import { isCustomized, loadProfile, sanitizeProfile, saveProfile } from './lib/profile';

  const MAX_SIZE = 100 * 1024 * 1024;
  const engine = new EngineClient();
  const initial = loadProfile();

  let profile = $state.raw<Profile>(initial.profile);
  let fromLink = $state(initial.fromLink);
  let phase = $state<'idle' | 'working' | 'report'>('idle');
  let report = $state.raw<CheckReport | null>(null);
  let error = $state<string | null>(null);
  let fileName = $state('');
  let requests = $state(0);
  let elapsed = $state(0);
  let profileOpen = $state(false);
  let dragging = $state(false);
  let source: ArrayBuffer | null = null;
  let importOpen = $state(false);
  let importText = $state('');
  let ruleCatalog = $state.raw<RuleInfo[]>([]);
  engine.rules().then(
    (list) => (ruleCatalog = list),
    () => {},
  );

  const custom = $derived(isCustomized(profile));
  const resources = (): number => performance.getEntriesByType('resource').length;

  async function run(buffer: ArrayBuffer, name: string): Promise<void> {
    phase = 'working';
    error = null;
    fileName = name;
    source = buffer.slice(0);
    const before = resources();
    const t0 = performance.now();
    try {
      const res = await engine.check(buffer, name, profile);
      elapsed = performance.now() - t0;
      requests = res.requests + (resources() - before);
      report = res.report;
      phase = 'report';
      window.scrollTo({ top: 0 });
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
      report = null;
      source = null;
      phase = 'idle';
    }
  }

  function openImport(text = ''): void {
    importText = text;
    importOpen = true;
  }

  /** Собранный из Markdown документ сразу проверяем, как обычный файл. */
  function checkBuilt(file: ArrayBuffer, name: string): void {
    importOpen = false;
    void run(file.slice(0), name);
  }

  async function openFile(file: File): Promise<void> {
    if (/\.(md|markdown|txt)$/i.test(file.name)) {
      openImport(await file.text());
      return;
    }
    if (file.size > MAX_SIZE) {
      error = 'Файл больше 100 МБ — браузер может с ним не справиться. Попробуйте сжать рисунки в Word.';
      return;
    }
    await run(await file.arrayBuffer(), file.name);
  }

  async function openSample(): Promise<void> {
    try {
      const res = await fetch('sample.docx');
      if (!res.ok) throw new Error(String(res.status));
      await run(await res.arrayBuffer(), 'Пример курсовой.docx');
    } catch {
      error = 'Не удалось открыть пример.';
    }
  }

  async function fix(request: FixRequest): Promise<FixResponse> {
    const res = await engine.fix(request, profile);
    const suffix = request.mode === 'tracked' ? 'правки ГОСТ' : request.mode === 'clean' ? 'исправлено по ГОСТ' : 'замечания ГОСТ';
    const caption =
      request.mode === 'none'
        ? `Замечаний на полях: ${res.commented}`
        : `Исправлено: ${res.fixed}${res.commented ? `, ещё ${res.commented} — в комментариях на полях` : ''}`;
    const delivered = await deliverFile(res.file, `${baseName(fileName)} (${suffix}).docx`, `${caption}. Требования: ${profile.name}.`);
    return { ...res, delivered };
  }

  function applyProfile(next: Profile): void {
    profile = sanitizeProfile(next);
    fromLink = false;
    saveProfile(profile);
    profileOpen = false;
    if (source && phase === 'report') void run(source.slice(0), fileName);
  }

  function reset(): void {
    phase = 'idle';
    report = null;
    error = null;
    source = null;
    window.scrollTo({ top: 0 });
  }

  // Файл можно бросить в любое место страницы.
  $effect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent): boolean => !!e.dataTransfer && [...e.dataTransfer.types].includes('Files');
    const enter = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      depth++;
      dragging = true;
    };
    const leave = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (!depth) dragging = false;
    };
    const over = (e: DragEvent): void => {
      if (hasFiles(e)) e.preventDefault();
    };
    const drop = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      dragging = false;
      const file = e.dataTransfer?.files[0];
      if (file && phase !== 'working') void openFile(file);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
    };
  });
</script>

<Header onHome={reset} onProfile={() => (profileOpen = true)} {custom} profileName={profile.name} />

<main>
  {#if phase === 'report' && report}
    <ReportView {report} {fileName} {requests} {elapsed} onReset={reset} onFix={fix} onProfile={() => (profileOpen = true)} />
  {:else}
    <Landing
      busy={phase === 'working'}
      {error}
      onFile={openFile}
      onSample={openSample}
      onProfile={() => (profileOpen = true)}
      onImport={() => openImport()}
      {custom}
      {fromLink}
      profileName={profile.name}
    />
  {/if}
</main>

{#if importOpen}
  <ImportDialog
    {profile}
    initialText={importText}
    onBuild={(r) => engine.markdown(r, profile)}
    onCheck={checkBuilt}
    onProfile={() => (profileOpen = true)}
    onClose={() => (importOpen = false)}
  />
{/if}

<Footer />

{#if profileOpen}
  <ProfileDialog {profile} rules={ruleCatalog} hasFile={phase === 'report'} onApply={applyProfile} onClose={() => (profileOpen = false)} />
{/if}

{#if dragging}
  <div class="drag" aria-hidden="true">
    <div class="drag-card">Отпустите файл, чтобы проверить его</div>
  </div>
{/if}

<style>
  main {
    min-height: calc(100vh - 160px);
  }

  .drag {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: grid;
    place-items: center;
    padding: 24px;
    background: color-mix(in srgb, var(--accent) 16%, rgb(0 0 0 / 30%));
    backdrop-filter: blur(3px);
  }

  .drag-card {
    padding: 44px 56px;
    border: 2px dashed var(--accent);
    border-radius: 22px;
    background: var(--surface);
    box-shadow: var(--shadow);
    color: var(--accent);
    font-size: 20px;
    font-weight: 640;
  }
</style>
